import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Note } from '@app/models/note.model';
import { Profile } from '@app/models/profile.model';
import {
  MenuParam,
  RecordField,
  RecordList,
  RecordTitle,
  RecordTitleSet,
} from '@app/models/record.model';
import { filter, Observable } from 'rxjs';
import { DateService } from './date.service';
import { DialogService } from './dialog.service';
import { UnitService } from './unit.service';
import { LibService } from './lib.service';
import { UserService } from './dai.service';
import { NoteOperation } from '@app/shared/enum/schedule';

interface FormValue {
  summary: boolean;
  handover: boolean;
  startDate: { date: Date; time: string };
  endDate: { date: Date; time: string };
  odrstfcd: string;
  sndstfcd: string;
}

interface Title {
  ttlkbn: string;
  ttlcd: string;
  noteid?: number;
}

interface RecordDelete {
  ttlnm: string;
  ownstfnm?: string;
  sdate?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class RecordService {
  constructor(
    private fb: FormBuilder,
    private unitService: UnitService,
    private dateService: DateService,
    private dialog: DialogService,
    private libservice: LibService,
    private userService: UserService
  ) { }

  delete(record: RecordDelete, username: string): Observable<any> {
    const date = record.sdate
      ? this.dateService.format(record.sdate, 'yy/MM/dd HH:mm')
      : '';
    const content = `以下の記録を削除します。
    <table>
    <tr><th>【利用者名】</th><td>${username}</td></tr>
    <tr><th>【記録日】</th><td>${date}</td></tr>
    <tr><th>【タイトル】</th><td>${record.ttlnm}</td></tr>
    <tr><th>【記録者】</th><td>${record.ownstfnm ?? ''}</td></tr>
    </table>
    削除してもよろしいですか？`;
    const data = {
      cancelText: 'キャンセル',
      excuteText: '削除',
      content,
    };
    const dialogRef = this.dialog.confirm(data);
    return dialogRef.afterClosed().pipe(filter((result) => !!result));
  }

  facesheetDelete(record: RecordDelete, username: string): Observable<any> {
    const date = record.sdate
      ? this.dateService.format(record.sdate, 'yy/MM/dd HH:mm')
      : '';
    const content = `<div>
    以下の記録を削除します。
    <table>
    <tr><th>【対象者】</th><td>${username}</td></tr>
    <tr><th>【記録日】</th><td>${date}</td></tr>
    <tr><th>【タイトル】</th><td>${record.ttlnm}</td></tr>
    <tr><th>【記録者】</th><td>${record.ownstfnm ?? ''}</td></tr>
    </table>
    削除してもよろしいですか？
    </div>`;
    const data = {
      cancelText: 'キャンセル',
      excuteText: '削除',
      content,
    };
    const dialogRef = this.dialog.confirm(data);
    return dialogRef.afterClosed().pipe(filter((result) => !!result));
  }

  isDelete(delstat?: number): boolean {
    return Boolean(delstat);
  }

  isDeleteAddedRecord({
    delstat,
    noteid,
  }: {
    delstat: number;
    noteid?: number;
  }) {
    return !noteid && this.isDelete(delstat);
  }

  getOperation(noteid: number | undefined) {
    if (noteid) return NoteOperation.UPDATE;
    return NoteOperation.INSERT;
  }

  isPersonal(menuGroupType?: string) {
    const list = ['NOTEEDITOR', 'PLANEDITOR'];
    return list.includes(menuGroupType ?? '');
  }

  isValidationError(noteDatas: Note[]): boolean {
    return noteDatas.some(
      (noteData) => !noteData.v2contents && !noteData.contents
    );
  }

  findNoteData(noteDatas: Note[]) {
    const list = [...noteDatas];
    // check case noteDatas is empty, null or contain null data
    if (!noteDatas || noteDatas?.length === 0 || noteDatas?.some((x) => { return (!x.v2contents && !x.contents) })) {
      // create location
      let location = 'findNoteData';
      let notesfind = noteDatas.filter((x) => { return (!x.v2contents && !x.contents) });
      let contents = noteDatas?.length === 0
        ? 'noteData empty from library'
        : notesfind.map((note) => { return `nulled when create ${note.ttlkbn}-${note.ttlcd}` }).join(' - ');
      this.libservice.errorLog(location, contents);
    }
    //
    return ({ ttlkbn, ttlcd, noteid }: Title) => {
      const index = list.findIndex((data) => {
        if (!!noteid) return noteid === data.noteid;
        return (
          data.ttlkbn === ttlkbn && data.ttlcd === ttlcd && data.delstat !== 1
        );
      });
      return index >= 0 ? list.splice(index, 1)[0] : [];
    };
  }

  hasTitle(titleSetList: RecordTitleSet[]) {
    return ({ ttlkbn, ttlcd }: { ttlkbn: string; ttlcd: string }): boolean => {
      return titleSetList.some((titleSet) =>
        titleSet.titles.some(
          (title) => title.ttlkbn === ttlkbn && title.ttlcd === ttlcd
        )
      );
    };
  }

  getRecords(noteList: Note[]): Note[] {
    return noteList.filter((item) => item.notekbn === 0);
  }

  getTitleSetKeys(titleSets: RecordTitle[]) {
    return titleSets.map((title) => {
      const { ttlkbn, ttlcd } = title;
      const obj = this.unitService.getObjcd(title);
      return { obj, kbn: ttlkbn, cd: ttlcd };
    });
  }

  setRecordForm(
    profile: Profile | null,
    record: RecordField
  ) {
    const { lifeflg, pickupflg, sdate, edate, odrstfcd, subject } = record;
    return this.fb.group({
      summary: Boolean(lifeflg),
      handover: Boolean(pickupflg),
      startDate: this.fb.group({
        date: [sdate, Validators.required],
        time: [
          sdate ? this.dateService.format(sdate, 'HH:mm') : null,
          Validators.required,
        ],
      }),
      endDate: this.fb.group({
        date: [edate, Validators.required],
        time: [
          edate ? this.dateService.format(edate, 'HH:mm') : null,
          Validators.required,
        ],
      }),
      caretime: '0分',
      odrstfcd: odrstfcd ?? profile?.staffCode,
      subject: subject || '',
      noteData: record
    });
  }

  getFormValue(
    { summary, handover, startDate, endDate, odrstfcd }: FormValue,
    getStaffName: (stafcd: string) => string | null
  ) {
    return {
      lifeflg: Number(summary),
      pickupflg: Number(handover),
      sdate: this.dateService.parseDateForm(startDate),
      edate: this.dateService.parseDateForm(endDate),
      odrstfcd,
      odrstfnm: getStaffName(odrstfcd),
    };
  }

  getBackPage({ ckbnId, id, ctype }: MenuParam) {
    const type = !!ctype ? 'ctype' : 'action';
    return ckbnId && id ? `record/list/${ckbnId}/${id}/${type}` : 'home/daily';
  }

  groupByDaily(recordList: RecordList[]) {
    const currentDate = this.dateService.format(new Date(), 'yyyy-MM-dd');
    return groupBy(recordList, (item) =>
      this.dateService.format(item.note.sdate, 'yyyy-MM-dd')
    ).map(([dateString, list]) => ({
      date: new Date(dateString),
      current: dateString === currentDate,
      userNotes: groupBy(list, (item) => item.note.objcd).map(
        ([_, list]) => ({
          username: this.userService.daiInfo(list[0].note.objcd)?.name || list[0].note.objnm,
          notes: list.map((item) => item.note),
        })
      ),
    }));
  }
}

const groupBy = <K, V>(
  array: readonly V[],
  getKey: (cur: V, idx: number, src: readonly V[]) => K
): [K, V[]][] =>
  Array.from(
    array.reduce((map, cur, idx, src) => {
      const key = getKey(cur, idx, src);
      const list = map.get(key);
      if (list) list.push(cur);
      else map.set(key, [cur]);
      return map;
    }, new Map<K, V[]>())
  );
